/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onMounted,
  ref,
  shallowRef,
  triggerRef,
} from "vue";
import { LemmonGrid, useLocalization, type GridSchema } from "@lemmonhq/uikit";
import {
  RecipeEditorModal,
  costColumns,
  recipeColumn,
  useRecipeCosts,
} from "../components/RecipeEditor";
import { Loading } from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Category, Extra, Service } from "@lemmonhq/types";
import { formatPrice } from "@lemmonhq/orders";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

const buildSchema = (colorPresets: string[]): GridSchema<Extra> => ({
  primaryKey: "id",
  columns: [
    {
      key: "id",
      header: "ID",
      type: "readonly",
      width: 72,
      frozen: true,
      cellClass: "font-mono text-xs text-base-content/50",
    },
    {
      key: "title",
      header: "Title",
      type: "text",
      width: 240,
      frozen: true,
      editable: true,
      bulkEditable: true,
      required: true,
      placeholder: "Extra title",
      cellClass: "font-semibold !text-accent",
    },
    {
      key: "description",
      header: "Description",
      type: "text",
      width: 340,
      editable: true,
      multiline: true,
      placeholder: "Short description",
    },
    {
      key: "price",
      header: "Price",
      type: "price",
      width: 120,
      editable: true,
      min: 0,
      step: 0.01,
      precision: 2,
      placeholder: "0.00",
      cellClass: "font-mono",
      formatter: (value) => {
        if (value === null || value === undefined || value === "") return "";
        const num = parseFloat(value);
        return isNaN(num) ? "" : formatPrice(num, false, 2, "CHF");
      },
      parser: (value) => {
        const cleaned = String(value).replace(/[^0-9.]/g, "");
        return cleaned ? parseFloat(cleaned) : null;
      },
    },
    {
      key: "status",
      header: "Status",
      type: "select",
      width: 150,
      editable: true,
      bulkEditable: true,
      options: [
        { value: "available", label: "Available", icon: "check_circle" },
        { value: "unavailable", label: "Unavailable", icon: "cancel" },
      ],
    },
    {
      key: "color",
      header: "Color",
      type: "color",
      width: 130,
      editable: true,
      colorPresets,
    },
  ],
});

export const ExtrasSubPage = defineComponent({
  name: "ExtrasSubPage",
  setup() {
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const error = ref<string | null>(null);
    const data = shallowRef<Extra[]>([]);
    const allCategories = ref<Category[]>([]);
    const allServices = ref<Service[]>([]);
    const changedRowIds = ref<Set<number | string>>(new Set());
    const saving = ref(false);

    const hasChanges = computed(() => changedRowIds.value.size > 0);
    const { t } = useLocalization();
    const recipeExtra = ref<Extra | null>(null);
    const recipeCosts = useRecipeCosts();

    // Suggestions for the color picker: colors already used by other extras
    // (this hierarchy level) plus colors already used by categories and
    // services (the parent hierarchy levels).
    const schema = computed(() => {
      const colorPresets = Array.from(
        new Set(
          [
            ...data.value.map((e) => e.color),
            ...allCategories.value.map((c) => c.color),
            ...allServices.value.map((s) => s.color),
          ].filter((c): c is string => Boolean(c)),
        ),
      );
      const base = buildSchema(colorPresets);
      return {
        ...base,
        columns: [
          ...base.columns,
          recipeColumn<Extra>(t, (row) => (recipeExtra.value = row)),
          ...costColumns<Extra>(
            t,
            (row) => recipeCosts.costFor("extra", row.id),
            (row) => (row.price == null ? null : Number(row.price) || null),
          ),
        ],
      };
    });

    onMounted(async () => {
      try {
        const [extras] = await Promise.all([
          api.getExtras(),
          trpc.categories.list.query().then((cats) => {
            allCategories.value = cats as Category[];
          }),
          api.getServices().then((services) => {
            allServices.value = services;
          }),
        ]);
        data.value = extras.map((e) => ({ ...e }));
      } catch (e) {
        error.value = "Failed to load extras";
      } finally {
        loading.value = false;
      }
    });

    const handleCellChange = (
      rowId: string | number,
      field: string,
      value: any,
    ) => {
      const row = data.value.find((r) => r.id === rowId);
      if (!row) return;
      (row as any)[field] = value;
      changedRowIds.value = new Set([...changedRowIds.value, rowId]);
      triggerRef(data);
    };

    const handleBulkChange = (
      rowIds: Set<string | number>,
      field: string,
      value: any,
    ) => {
      for (const id of rowIds) handleCellChange(id, field, value);
    };

    const handleSave = async () => {
      saving.value = true;
      try {
        const changed = Array.from(changedRowIds.value)
          .map((id) => data.value.find((r) => r.id === id))
          .filter(Boolean) as Extra[];

        await Promise.all(
          changed.map((extra) =>
            trpc.extras.update.mutate({
              id: extra.id,
              title: extra.title,
              description: extra.description,
              status: extra.status,
              color: extra.color,
              price:
                extra.price === null || extra.price === undefined
                  ? null
                  : Number(extra.price),
            }),
          ),
        );

        changedRowIds.value = new Set();
        const extras = await api.getExtras();
        data.value = extras.map((e) => ({ ...e }));
        toast.showToast("Extras saved", { type: "success" });
      } catch (e) {
        toast.showToast("Failed to save extras", { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const handleDiscard = async () => {
      loading.value = true;
      try {
        const extras = await api.getExtras();
        data.value = extras.map((e) => ({ ...e }));
        changedRowIds.value = new Set();
      } finally {
        loading.value = false;
      }
    };

    return () => {
      if (loading.value) {
        return (
          <div class="flex h-full w-full items-center justify-center">
            <Loading />
          </div>
        );
      }

      if (error.value) {
        return (
          <div class="flex h-full w-full items-center justify-center text-error">
            {error.value}
          </div>
        );
      }

      return (
        <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
          {/* Header */}
          <SubpageHeader
            title="Extras"
            subtitle="Double-click or press Enter to edit · Tab / arrows to navigate"
            v-slots={{
              badges: () => (
                <>
                  <span class="rounded-md border border-base-300 bg-base-100 px-2 py-0.5 text-xs font-medium text-base-content/60">
                    {data.value.length}
                  </span>
                  {hasChanges.value && (
                    <span class="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {changedRowIds.value.size} changed
                    </span>
                  )}
                </>
              ),
              default: () => (
                <>
                  <div class="flex items-center gap-2">
                    {hasChanges.value && (
                      <>
                        <button
                          class="btn btn-ghost btn-sm text-base-content/70"
                          onClick={handleDiscard}
                        >
                          <span class="material-symbols-outlined text-base">
                            undo
                          </span>
                          Discard
                        </button>
                        <button
                          class="btn btn-primary btn-sm"
                          disabled={saving.value}
                          onClick={handleSave}
                        >
                          <span class="material-symbols-outlined text-base">
                            save
                          </span>
                          {saving.value ? "Saving…" : "Save changes"}
                        </button>
                      </>
                    )}
                  </div>
                </>
              ),
            }}
          />

          {/* Grid */}
          <div class="flex-1 overflow-hidden p-3">
            <LemmonGrid
              data={data.value}
              schema={schema.value}
              changedRowIds={changedRowIds.value}
              onCellChange={handleCellChange}
              onBulkChange={handleBulkChange}
            />
          </div>
          {recipeExtra.value && (
            <RecipeEditorModal
              entityType="extra"
              entityId={recipeExtra.value.id}
              entityName={recipeExtra.value.title}
              price={
                recipeExtra.value.price == null
                  ? null
                  : Number(recipeExtra.value.price)
              }
              onClose={() => {
                recipeExtra.value = null;
                recipeCosts.reload();
              }}
            />
          )}
        </div>
      );
    };
  },
});
